import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { action } from '@/shared/screen';

import { isHomeHref } from './home-links';

// Ações da Início (U23/U24): abrir um destino mostrado, fixar, tirar e reordenar favoritos e perguntar ao
// Luy. Os favoritos chegam por uma porta que a casca liga (`setFavoritesPort`): o manifesto entra no
// registro de telas, e o armazenamento dos favoritos lê o registro (sem ciclo).

export interface FavoritesPort {
  /** Fixa no fim (ou na `position`, 0 = primeira); `false` se não deu. */
  readonly pin: (screenId: string, position?: number) => boolean;
  /** Tira; devolve a posição em que estava. */
  readonly unpin: (screenId: string) => number | undefined;
  readonly move: (screenId: string, position: number) => boolean;
  readonly pinned: () => readonly string[];
  /** Nome da tela no idioma ativo, só se ela pode ser fixada por quem vê. */
  readonly titleOf: (screenId: string) => string | undefined;
}

let port: FavoritesPort | undefined;

export function setFavoritesPort(next: FavoritesPort | undefined) {
  port = next;
}

/* eslint-disable lingui/no-unlocalized-strings -- respostas ao modelo, inglês canônico (U33) */
const NO_PORT = 'Favorites are only available inside the signed-in UYZY shell.';
const UNKNOWN_SCREEN = 'Unknown screen, or a screen that cannot be pinned (it needs a record) or that the person cannot open.';
const NOT_PINNED = 'This screen is not pinned on the home screen.';
const PIN_FAILED = 'Could not pin: the screen is already pinned or the home screen already has 12 pinned screens.';
const ONLY_HOME = 'Asking Luy with a suggested question only works from the home screen.';
/* eslint-enable lingui/no-unlocalized-strings */

function required(): FavoritesPort {
  if (!port) throw new Error(NO_PORT);
  return port;
}

function titled(screenId: string): string {
  const title = required().titleOf(screenId);
  if (!title) throw new RangeError(UNKNOWN_SCREEN);
  return title;
}

export const homeOpen = action<{ href: string }>({
  id: 'home.open',
  kind: 'navigate',
  label: msg`Abrir`,
  description: msg`Abre, na mesma aba, um destino que a Início mostra agora: a lista já filtrada de um cartão da Minha mesa, uma tela fixada nos Favoritos, um item dos Recentes ou o Dashboard do Pulso do dia. href é exatamente o caminho mostrado. Não muda nada.`,
  input: z.strictObject({ href: z.string().min(1).max(500).refine((href) => isHomeHref(href)) }),
  run: ({ input, nav }) => {
    if (!isHomeHref(input.href)) throw new RangeError('home.open: destino fora da Início');
    return nav.to(input.href);
  },
});

const screenInput = z.strictObject({ screenId: z.string().min(1).max(80) });

export const homeFavoritesPin = action<{ screenId: string }>({
  id: 'home.favorites.pin',
  kind: 'write',
  label: msg`Fixar na Início`,
  description: msg`Fixa uma tela nos Favoritos da Início, no fim da lista. screenId é o id de uma tela do UYZY que abre sem registro (como as listas). Guarda só o id da tela, neste navegador, para esta pessoa e este correspondente. Pede a confirmação da pessoa.`,
  input: screenInput,
  preview: ({ screenId }) => {
    const title = titled(screenId);
    return { title: i18n._(msg`Fixar ${title} na Início?`), detail: i18n._(msg`A tela aparece nos Favoritos da Início, neste navegador.`) };
  },
  run: ({ input }) => {
    titled(input.screenId);
    if (!required().pin(input.screenId)) throw new Error(PIN_FAILED);
  },
});

export const homeFavoritesUnpin = action<{ screenId: string }>({
  id: 'home.favorites.unpin',
  kind: 'write',
  label: msg`Remover da Início`,
  description: msg`Tira uma tela dos Favoritos da Início. A tela continua no menu e na busca. Pede a confirmação da pessoa.`,
  input: screenInput,
  preview: ({ screenId }) => {
    const title = required().titleOf(screenId) ?? screenId;
    return { title: i18n._(msg`Remover ${title} da Início?`), detail: i18n._(msg`A tela continua no menu e na busca.`) };
  },
  run: ({ input }) => {
    const index = required().unpin(input.screenId);
    if (index === undefined) throw new Error(NOT_PINNED);
    return { screenId: input.screenId, index };
  },
});

/** Desfazer do "Remover da Início": volta a tela para a posição em que estava. */
export function restoreFavorite(result: unknown): void {
  const value = result as { readonly screenId?: unknown; readonly index?: unknown } | undefined;
  if (typeof value?.screenId === 'string' && typeof value.index === 'number') required().pin(value.screenId, value.index);
}

export const homeFavoritesMove = action<{ screenId: string; position: number }>({
  id: 'home.favorites.move',
  kind: 'write',
  label: msg`Reordenar favoritos`,
  description: msg`Leva uma tela fixada para outra posição nos Favoritos da Início (position começa em 1, a primeira). Pede a confirmação da pessoa.`,
  input: z.strictObject({ screenId: z.string().min(1).max(80), position: z.number().int().min(1).max(12) }),
  preview: ({ screenId, position }) => {
    const title = required().titleOf(screenId) ?? screenId;
    return { title: i18n._(msg`Levar ${title} para a posição ${position} dos favoritos?`) };
  },
  run: ({ input }) => {
    if (!required().pinned().includes(input.screenId)) throw new Error(NOT_PINNED);
    required().move(input.screenId, input.position - 1);
  },
});

export const homeAskLuy = action<{ question: string }>({
  id: 'home.askLuy',
  kind: 'draft',
  label: msg`Perguntar ao Luy`,
  description: msg`Abre o Luy no painel ao lado já perguntando uma das perguntas sugeridas na Início (question é o texto exato de um dos botões). Sem acesso ao Luy, o painel abre explicando o porquê e nada é enviado.`,
  input: z.strictObject({ question: z.string().min(1).max(200) }),
  run: () => {
    throw new Error(ONLY_HOME);
  },
});

export const HOME_ACTIONS = [homeOpen, homeFavoritesPin, homeFavoritesUnpin, homeFavoritesMove, homeAskLuy];
